import React, { useState, useRef, useCallback, useEffect } from 'react';
import Button from 'view/components/buttons/Button';
import { faCommentAlt, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import FeedbackHighlighter from './FeedbackHighlighter';
import { formatTextWithHighlights } from 'utils/feedbackUtils';
import IconButton from 'view/components/buttons/IconButton';
import AnnotationModal from './AnnotationModal';
import AssignmentFeedback from 'models/assignments/AssignmentFeedback';
import { WritingType } from 'view/components/student/assignment/writing/WritingAssignment';

interface TeacherWritingGraderProps {
  submissionId: string;
  questionId: string;
  studentResponse: string;
  teacherView: boolean;
  writingType: WritingType;
  onSave?: () => void;
  onAddFeedback?: (feedback: AssignmentFeedback) => void;
  onShowBook?: () => void;
}

const TeacherWritingGrader: React.FC<TeacherWritingGraderProps> = ({
  submissionId,
  studentResponse,
  teacherView,
  writingType,
  questionId,
  onSave,
  onAddFeedback,
  onShowBook,
}) => {
  const [selection, setSelection] = useState<{
    text: string;
    startIndex: number;
    endIndex: number;
  } | null>(null);
  const [showAnnotationForm, setShowAnnotationForm] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const feedbackHighlighterRef = useRef<any>(null); // optional: tighten with real type

  const handleTextSelection = useCallback((event: MouseEvent) => {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (!range || range.collapsed) return;

      if (
        contentRef.current &&
        contentRef.current.contains(range.commonAncestorContainer)
      ) {
        const selectedText = selection.toString().trim();
        if (selectedText) {
          const plainText = contentRef.current.textContent || '';
          const startIndex = plainText.indexOf(selectedText);
          const endIndex = startIndex + selectedText.length;

          setSelection({ text: selectedText, startIndex, endIndex });
          setShowAnnotationForm(true);
        }
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    return () => document.removeEventListener('mouseup', handleTextSelection);
  }, [handleTextSelection]);

  const handleAddComment = () => {
    setShowAnnotationForm(true);
  };

  const handleAnnotationModalClosed = () => {
    setShowAnnotationForm(false);
    setSelection(null);
  };

  return (
    <>
      <div className="feedback-highlighter" ref={contentRef}>
        <FeedbackHighlighter
          ref={feedbackHighlighterRef}
          submissionId={submissionId}
          contentText={studentResponse}
          formatTextWithHighlights={formatTextWithHighlights}
        />
        {teacherView && (
          <IconButton
            type="transparent"
            onClick={handleAddComment}
            icon={faCommentAlt}
            color="var(--dark-blue)"
            title="Add a comment"
            className="edit-tool"
          />
        )}
      </div>

      {teacherView && (
        <AnnotationModal
          submissionId={submissionId}
          questionId={questionId}
          isOpen={showAnnotationForm}
          onClose={handleAnnotationModalClosed}
          onSubmit={onAddFeedback}
          selection={selection}
        />
      )}

      {writingType === WritingType.COMPREHENSION ? (
        <div className="reading-footer">
          <Button
            type="edit"
            onClick={onShowBook}
            label="To reading"
            fontAwesomeIcon={faArrowLeft}
          />
        </div>
      ) : null}
    </>
  );
};

export default TeacherWritingGrader;
